import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
    Select, MenuItem, FormControl, InputLabel, Typography, Chip,
    CircularProgress, Alert, TablePagination, Tooltip, IconButton
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFirebase } from '../../../context/FirebaseContext';
import { getAlphaWaitlist, getAlphaWaves, inviteFromWaitlist, promoteWaitlistEntry, deleteWaitlistEntry } from '../../../api/alpha';
import logger from '../../../utils/logger';

const WaitlistManagement = ({ onUpdate }) => {
    const { auth } = useFirebase();
    const [entries, setEntries] = useState([]);
    const [waves, setWaves] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalCount, setTotalCount] = useState(0);
    const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
    const [bulkFormData, setBulkFormData] = useState({
        count: 10,
        waveId: ''
    });

    const loadWaves = useCallback(async () => {
        try {
            const response = await new Promise((resolve) => {
                getAlphaWaves(auth, resolve);
            });
            if (Array.isArray(response)) {
                setWaves(response);
            } else if (response.data) {
                setWaves(response.data);
            }
        } catch (err) {
            logger.error('Error loading waves:', err);
        }
    }, [auth]);

    const loadEntries = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await new Promise((resolve) => {
                getAlphaWaitlist(auth, resolve, page, rowsPerPage);
            });
            if (response.error) {
                setError(response.error);
            } else if (response.content) {
                // Spring Page format
                setEntries(response.content);
                setTotalCount(response.totalElements || 0);
            } else if (response.items) {
                // Custom response format
                setEntries(response.items);
                setTotalCount(response.total || response.items.length);
            } else if (Array.isArray(response)) {
                setEntries(response);
                setTotalCount(response.length);
            }
        } catch (err) {
            logger.error('Error loading waitlist:', err);
            setError('Failed to load waitlist');
        } finally {
            setLoading(false);
        }
    }, [auth, page, rowsPerPage]);

    useEffect(() => {
        loadWaves();
    }, [loadWaves]);

    useEffect(() => {
        loadEntries();
    }, [loadEntries]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handlePromoteEntry = async (entryId, waveId) => {
        try {
            setLoading(true);
            const response = await new Promise((resolve) => {
                promoteWaitlistEntry(auth, resolve, entryId, waveId);
            });
            if (response.error) {
                setError(response.error);
            } else {
                await loadEntries();
                onUpdate?.();
            }
        } catch (err) {
            logger.error('Error promoting entry:', err);
            setError('Failed to promote entry');
        } finally {
            setLoading(false);
        }
    };

    const handleBulkInvite = async () => {
        try {
            setLoading(true);
            const response = await new Promise((resolve) => {
                inviteFromWaitlist(auth, resolve, bulkFormData.count, bulkFormData.waveId);
            });
            if (response.error) {
                setError(response.error);
            } else {
                setBulkDialogOpen(false);
                await loadEntries();
                onUpdate?.();
            }
        } catch (err) {
            logger.error('Error bulk inviting:', err);
            setError('Failed to send bulk invites');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteEntry = async (entryId) => {
        if (!window.confirm('Are you sure you want to delete this waitlist entry?')) return;

        try {
            setLoading(true);
            const response = await new Promise((resolve) => {
                deleteWaitlistEntry(auth, resolve, entryId);
            });
            if (response?.error) {
                setError(response.error);
            } else {
                await loadEntries();
                onUpdate?.();
            }
        } catch (err) {
            logger.error('Error deleting entry:', err);
            setError('Failed to delete entry');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'warning';
            case 'invited': return 'info';
            case 'converted': return 'success';
            default: return 'default';
        }
    };

    const formatDate = (dateArray) => {
        if (!dateArray || !Array.isArray(dateArray)) return 'N/A';
        const [year, month, day, hour, minute] = dateArray;
        return new Date(year, month - 1, day, hour || 0, minute || 0).toLocaleString();
    };

    const activeWaves = waves.filter(w => w.status === 'active');

    if (loading && entries.length === 0) {
        return (
            <Box className="alpha-loading">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box className="waitlist-management">
            <Box className="alpha-section-header">
                <Typography variant="h6">
                    Waitlist
                    {totalCount > 0 && (
                        <Chip
                            label={`${totalCount} entries`}
                            size="small"
                            sx={{ ml: 1 }}
                        />
                    )}
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={() => setBulkDialogOpen(true)}
                    size="small"
                    disabled={entries.length === 0 || activeWaves.length === 0}
                >
                    Bulk Invite
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {activeWaves.length === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    No active waves available. Create and activate a wave before inviting users.
                </Alert>
            )}

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Email</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Position</TableCell>
                            <TableCell>Referrals</TableCell>
                            <TableCell>Joined</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {entries.map((entry) => (
                            <TableRow key={entry.id}>
                                <TableCell>
                                    <Typography variant="body2">
                                        {entry.email}
                                    </Typography>
                                </TableCell>
                                <TableCell>{entry.name || '-'}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={entry.status}
                                        color={getStatusColor(entry.status)}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>#{entry.position || '-'}</TableCell>
                                <TableCell>
                                    {entry.referralCount > 0 ? (
                                        <Chip
                                            label={entry.referralCount}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    ) : '-'}
                                </TableCell>
                                <TableCell>{formatDate(entry.createdAt)}</TableCell>
                                <TableCell>
                                    {entry.status === 'waiting' && activeWaves.length > 0 && (
                                        <FormControl size="small" sx={{ minWidth: 100 }}>
                                            <Select
                                                displayEmpty
                                                value=""
                                                onChange={(e) => handlePromoteEntry(entry.id, e.target.value)}
                                                renderValue={() => (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <PersonAddIcon fontSize="small" />
                                                        <span>Invite</span>
                                                    </Box>
                                                )}
                                            >
                                                {activeWaves.map((wave) => (
                                                    <MenuItem key={wave.id} value={wave.id}>
                                                        {wave.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                    {entry.status === 'invited' && (
                                        <Tooltip title={`Invited on ${formatDate(entry.invitedAt)}`}>
                                            <Chip label="Invited" size="small" variant="outlined" />
                                        </Tooltip>
                                    )}
                                    {entry.status === 'converted' && (
                                        <Chip label="Converted" size="small" color="success" variant="outlined" />
                                    )}
                                    <Tooltip title="Delete entry">
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleDeleteEntry(entry.id)}
                                            sx={{ ml: 1 }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                        {entries.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <Typography variant="body2" color="textSecondary">
                                        No waitlist entries yet
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                {totalCount > rowsPerPage && (
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        component="div"
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                )}
            </TableContainer>

            {/* Bulk Invite Dialog */}
            <Dialog open={bulkDialogOpen} onClose={() => setBulkDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Bulk Invite from Waitlist</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        This will send invite codes to the top users on the waitlist (ordered by position,
                        which factors in referral count and signup date).
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <FormControl fullWidth required>
                            <InputLabel>Wave</InputLabel>
                            <Select
                                value={bulkFormData.waveId}
                                onChange={(e) => setBulkFormData({ ...bulkFormData, waveId: e.target.value })}
                                label="Wave"
                            >
                                {activeWaves.map((wave) => (
                                    <MenuItem key={wave.id} value={wave.id}>
                                        {wave.name} ({wave.currentCount || 0}/{wave.capacity || '∞'})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Number of Invites"
                            type="number"
                            value={bulkFormData.count}
                            onChange={(e) => setBulkFormData({ ...bulkFormData, count: parseInt(e.target.value) })}
                            fullWidth
                            inputProps={{ min: 1, max: 100 }}
                            helperText="Max 100 per batch"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleBulkInvite}
                        disabled={!bulkFormData.waveId || bulkFormData.count < 1}
                    >
                        Send Invites
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default WaitlistManagement;
